// ContentGrid.js
import React, { useState, useEffect, useRef,  } from 'react';

import {
  Box,
  CircularProgress,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Button,
  Breadcrumbs,
  Link,
  Grid,
  Card,
  CardActionArea
} from '@mui/material';

import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HomeIcon from '@mui/icons-material/Home';
import CloseIcon from '@mui/icons-material/Close';

// Fixed navigation structure for now
const ContentTreeBase = {
  Library: {
    "100+": {}, // for 100+ phrases
    Numbers: {
      Cardinal: {},
      Ordinal: {},
    },
    Conversations: {},
  },
  MyContent: {
    All: {},
    // Conversations: {}
  },
  // Shared: {}
};

const ContentGrid = ({ contentPath, handleContentChoiceChange, disabled, allContentTags }) => {
  const [openContentDialog, setOpenContentDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [path, setPath] = useState([]); // navigation path, e.g. ["Library","Numbers"]
  const [selectedPath, setSelectedPath] = useState(null); // holds currently toggled path
  // preserve the original contentPath passed to the component for later comparison
  const origContentPathRef = useRef(contentPath);
  const [contentTree, setContentTree] = useState({...ContentTreeBase})

  const isDisabled = Boolean(disabled);

  // Parse contentPath like "100+" or "numbers/cardinal" or "MyContent"
  // into a path array that matches contentTree keys, e.g. ["Library","Numbers","Cardinal"]
  const parseContentPath = (cp) => {
    if (!cp) return [];
    const s = String(cp).trim();
    if (s.length === 0) return [];
  
    const parts = s.split('/').map((p) => p.trim()).filter(Boolean);
    let node = contentTree;
    let out = [];

    if ( !['mycontent', 'shared'].includes(parts[0].toLowerCase()) ) {
      out = ['Library']
      node = contentTree.Library;
    }

    for (const part of parts) {
      const match = Object.keys(node).find(
        (k) => k.toLowerCase() === part.toLowerCase()
      );
      if (!match) break;
      out.push(match);
      node = node[match] || {};
    }
    return out;
  };

  // Convert path array to the value string we use outside
  const pathArraytoContentPath = (p = path) => {
    let value = '';
    const first = p && p.length > 0 ? p[0] : '';

    if (first === 'Library') {
      value = p.slice(1).join('/').toLowerCase();
    } else if (first === 'MyContent') {
      // value = 'MyContent';
      value = p.join('/');
    } else {
      value = '';
    }
    return value;
  };

  useEffect(() => {
    const startPath = parseContentPath(contentPath);
    setPath(startPath);
    setSelectedPath(null); // reset selection when external value changes
    console.log('tags in ContentGrid:', allContentTags)
  }, [contentPath, allContentTags]);

  const handleClickOpen = () => {
    if (isDisabled) return; // guard against clicks when disabled
    // open dialog at location matching contentPath
    const fullPath = parseContentPath(contentPath);
    const startPath = fullPath.slice(0, -1);
    setPath(startPath);
    // if contentPath points to a leaf, set selectedPath so it's highlighted
    if (fullPath.length > 0) {
      setSelectedPath(fullPath);
    } else {
      setSelectedPath(null);
    }
    setOpenContentDialog(true);
  };

  const handleCancel = () => {
    // user clicked outside → restore original
    const originalContentPath = origContentPathRef.current;
    setPath(parseContentPath(originalContentPath));
    setSelectedPath(null);
    setOpenContentDialog(false);
  };

  const handleSelect = () => {
    if (selectedPath) {
      const value = pathArraytoContentPath(selectedPath);
      const originalContentPath = origContentPathRef.current;
      if (value !== '' && value !== originalContentPath) {
        handleContentChoiceChange({ target: { value } });
      }
    }
    setOpenContentDialog(false);
  };
  const handleSelectPath = (pathToSelect) => {
    if (!pathToSelect) return;
    const value = pathArraytoContentPath(pathToSelect);
    const originalContentPath = origContentPathRef.current;
    if (value !== '' && value !== originalContentPath) {
      handleContentChoiceChange({ target: { value } });
    }
    setSelectedPath(pathToSelect);
    setOpenContentDialog(false);
  };

  const handleBack = () => {
    if (path.length > 0) {
      setPath(path.slice(0, -1));
    }
  };

  // Helper to get current node based on path
  const getNode = (tree, pathParts) => {
    let node = tree;
    for (const part of pathParts) {
      node = node?.[part] || {};
    }
    return node;
  };

  const currentNode = getNode(contentTree, path);
  const children = Object.keys(currentNode);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      height="100%"
      textAlign="center"
    
    >
      <Tooltip title="Open Folder">
        <Button
          onClick={handleClickOpen}
          startIcon={<FolderOpenIcon />}
          variant="outlined"
          size="medium"
          disabled={isDisabled}
          sx={{ ml: 1, textTransform: 'none', borderRadius: 6, }}
        >
          {path.length === 0 ? 'Home' : path.join(' / ')}
        </Button>
      </Tooltip>

      <Dialog
        open={openContentDialog}
        onClose={handleCancel}
        fullWidth
        maxWidth="sm"
        PaperProps={{
                sx: {
                    backgroundColor: 'seashell',
                    color: 'black',
                    borderRadius: '10px',
                    p: 1,
                },
            }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Contents
          <IconButton
            aria-label="close"
            onClick={handleCancel}
            size="small"
            sx={{ ml: 2 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {/* Navigation controls */}
          <Box display="flex" alignItems="center" mb={2}>
            {/* {path.length > 0 && (
              <IconButton onClick={handleBack} size="small">
                <ArrowBackIcon />
              </IconButton>
            )} */}

            <Breadcrumbs aria-label="breadcrumb" sx={{ ml: 1 }}>
              <Link
                component="button"
                variant="body2"
                onClick={() => setPath([])}
                aria-label="Home"
              >
                <HomeIcon fontSize="small" />
              </Link>
              {path.map((segment, idx) => (
                <Link
                  component="button"
                  variant="body2"
                  key={idx}
                  onClick={() => setPath(path.slice(0, idx + 1))}
                >
                  {segment}
                </Link>
              ))}
            </Breadcrumbs>
          </Box>

          {/* Child tiles */}
          {children.length > 0 ? (
            <Grid container spacing={2}>
              {children.map((child) => {
                const childNode = currentNode[child] || {};
                const childHasChildren = Object.keys(childNode).length > 0;
                const newPath = [...path, child];
                const isSelected =
                  selectedPath?.join('/') === newPath.join('/');

                return (
                  <Grid item xs={6} sm={4} key={child}>
                    <Card
                      sx={{
                        position: 'relative',
                        minHeight: 96,
                      }}
                    >
                      <CardActionArea 
                        onClick={() => {
                          if (childHasChildren) {
                            // drill down
                            setPath(newPath);
                          } else {
                            // leaf → immediately select and close
                            handleSelectPath(newPath);
                          }
                        }}
                      >
                        <Box
                          p={2}
                          textAlign="center"
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {childHasChildren ? (
                            <FolderIcon fontSize="large" />
                          ) : ( 
                            <DescriptionIcon fontSize="large" />
                          )}
                          <Typography
                            variant="subtitle2"
                            sx={{
                              mt: 0.5,
                              px: 0.5,
                              wordBreak: 'break-word',
                              whiteSpace: 'normal',
                              textAlign: 'center',
                              maxWidth: '100%',
                            }}
                          >
                            {child}
                          </Typography>
                        </Box>
                      </CardActionArea>

                      {/* Only show select toggle if this tile has children */}
                      {!childHasChildren && (
                        <Button
                          size="small"
                          variant={isSelected ? 'contained' : 'outlined'}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            minWidth: 'auto',
                            padding: '2px 6px',
                            fontSize: '0.75rem',
                            lineHeight: 1
                          }}
                          onClick={(e) => {
                            e.stopPropagation(); // prevent drill down
                            handleSelectPath(newPath);
                          }}
                        >
                          ✓
                        </Button>
                      )}

                      {childHasChildren && (
                        <ChevronRightIcon
                          fontSize="small"
                          sx={{
                            position: 'absolute',
                            bottom: 8,
                            right: 8,
                            color: 'text.secondary'
                          }}
                        />
                      )}
                    </Card>
                  </Grid>
                );
              })}
                            </Grid>
                          ) : (
                            <Typography variant="body1" color="text.secondary">
                              Leaf node — connect to phrases here.
                            </Typography>
                          )}
                        </DialogContent>

                        <DialogActions>
                            {/* Selection happens via clicking a leaf tile or its check button; no explicit Select action needed. */}
                        </DialogActions>
                      </Dialog>
      </Box>
    );

  };

  export default ContentGrid;
